import React, { useEffect, useMemo, useState } from "react";
import { Resource } from "@/bryntum-lib/models/Resource";
import { Input } from "@/modern/components/ui/input";
import { Textarea } from "@/modern/components/ui/textarea";
import { Label } from "@/modern/components/ui/label";
import { Button } from "@/modern/components/ui/button";
import { Badge } from "@/modern/components/ui/badge";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { resourceSchema, ResourceFormData } from "@/react/schema/resourceSchema";
import { toast } from "sonner";
import { RESOURCE_LABEL_MAX_LENGTH } from "@/other/constants/OtherKeys";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import type { ISwivuStore } from "@/types/SwivuStore";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys";
import { ENTITY_CONFIGS } from "@/other/constants/EntityManagerConfig";
import { SwivuDuplicateEntityHelper } from "@/react/components/SwivuDuplicateEntityHelper";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { BaseModalBody } from "@/react/components/editors/base/BaseModalBody";
import { EntityPillBar } from "@/react/components/entityManager/components/EntityPillBar";
import { Loader2 } from "lucide-react";
import { useConfirm } from "@omit/react-confirm-dialog";

interface ResourceGeneralEditorProps {
  record: {
    resource: Resource;
    onAfterRecordAdd?: (resourceRecord: Resource) => void;
  };
  onClose: () => void;
  editorInstance?: {
    className?: string;
    [key: string]: unknown;
  };
}

const auditDateOptions: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };

const formatAuditDate = (dateValue?: string | number | Date): string => {
  if (!dateValue) {
    return "";
  }

  const parsedDate = new Date(dateValue);
  return isNaN(parsedDate.getTime()) ? "" : parsedDate.toLocaleDateString("en-US", auditDateOptions);
};

export const ResourceGeneralEditor = ({ record, onClose, editorInstance }: ResourceGeneralEditorProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const { project, entityManager } = useSwivuBryntumContext();
  const { resource: resourceRecord, onAfterRecordAdd } = record;
  const { subscribe } = useSwivuStore<Resource>({ storeId: STORE_KEYS.resourceStore });
  const confirm = useConfirm();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    trigger,
    formState: { errors, isDirty, isValid },
  } = useForm<ResourceFormData>({
    resolver: zodResolver(resourceSchema),
    defaultValues: {
      name: resourceRecord.name || "",
      description: resourceRecord.description || "",
      location: resourceRecord.location || "",
      statusId: resourceRecord.statusId || null,
      categoryId: resourceRecord.categoryId || null,
      teamId: resourceRecord.teamId || null,
      tagsId: resourceRecord.tagsId || null,
      iconId: resourceRecord.iconId || null,
      typeId: resourceRecord.typeId || null,
      jobTitle: resourceRecord.jobTitle || ""
    }
  });

  const nameValue = watch("name");

  useEffect(() => {
    return subscribe(({ record: changedRecord }) => {
      if (resourceRecord.id === changedRecord.id) {
        reset({
          name: changedRecord.name || "",
          description: changedRecord.description || "",
          location: changedRecord.location || "",
          statusId: changedRecord.statusId || null,
          categoryId: changedRecord.categoryId || null,
          teamId: changedRecord.teamId || null,
          tagsId: changedRecord.tagsId || null,
          iconId: changedRecord.iconId || null,
          typeId: changedRecord.typeId || null,
          jobTitle: changedRecord.jobTitle || ""
        });
        return true;
      }
      return false;
    });
  }, [resourceRecord, reset, subscribe]);

  const onSubmit = async (data: ResourceFormData) => {
    try {
      setIsSaving(true);
      resourceRecord.set(data);
      const isNewResource = resourceRecord.isPhantom;
      if (isNewResource) {
        project.resourceStore.add(resourceRecord);
        onAfterRecordAdd?.(resourceRecord);
      }

      await project.sync();
      toast.success("Resource saved successfully");
    } catch (error) {
      console.error("Error saving resource:", error);
      toast.error("Something went wrong while saving the resource. Please try again.", {
        description: "If the problem persists, please contact support."
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = async () => {
    if (isDirty) {
      const confirmed = await confirm({
        title: "Unsaved Changes",
        description: "You have unsaved changes. What would you like to do?",
        alertDialogOverlay: {
          className: "z-[--swivu-bryntum-z-index]"
        },
        alertDialogContent: {
          className: "z-[--swivu-bryntum-z-index]"
        },
        customActions: ({ confirm, cancel }: { confirm: () => void; cancel: () => void }) => (
          <div className="flex w-full justify-end gap-3">
            <Button variant="ghost" onClick={cancel} size="sm">
              Keep working
            </Button>
            <Button variant="destructive" onClick={confirm} size="sm">
              Do not save
            </Button>
            <Button
              variant="success"
              onClick={async () => {
                if (!isValid) {
                  await trigger("name");
                  cancel();
                  return;
                }
                handleSave();
                confirm();
              }}
              size="sm"
            >
              Save & close
            </Button>
          </div>
        )
      });

      if (confirmed) onClose();
      return;
    }
    onClose();
  };

  const handleSave = async () => {
    await handleSubmit(onSubmit)();
  };

  const openDashboard = () => {
    if (entityManager) {
      entityManager.openEntityManager({ entityType: ENTITY_TYPES.RESOURCE, entityId: resourceRecord.id });
    }
  }

  const handleSaveAndDashboard = async () => {
    await handleSave();
    if (isValid) {
      openDashboard();
      onClose();
    }
  };

  const hideDashboardButton = entityManager.isOpen && entityManager.entityId == resourceRecord.id;

  const resourceMetaItems = useMemo(() => {
    const items: Array<{ key: string; label: string; value: string }> = [];

    if (resourceRecord.creatorRecord?.name && resourceRecord.createdAt) {
      items.push({
        key: "created",
        label: "Created",
        value: `${resourceRecord.creatorRecord.name} - ${formatAuditDate(resourceRecord.createdAt)}`
      });
    }

    if (resourceRecord.updaterRecord?.name && resourceRecord.updatedAt) {
      items.push({
        key: "updated",
        label: "Updated",
        value: `${resourceRecord.updaterRecord.name} - ${formatAuditDate(resourceRecord.updatedAt)}`
      });
    }

    return items;
  }, [resourceRecord.createdAt, resourceRecord.creatorRecord?.name, resourceRecord.updatedAt, resourceRecord.updaterRecord?.name]);

  const headerRenderer = () => (
    <div className="drag-handle flex items-center justify-between p-5 border-b border-border/50 rounded-t-lg border-t-2">
      <h2 className="text-lg m-0">General Settings</h2>
      <button
        onClick={handleClose}
        className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-6 -top-5 -right-5 bg-background p-3 rounded-full border opacity-100 !size-12"
      >
        <i className="fa-light fa-times text-swivu-icon" />
        <span className="sr-only">Close</span>
      </button>
    </div>
  );

  return (
    <BaseModal
      record={resourceRecord}
      onClose={handleClose}
      title=""
      description=""
      editorInstance={editorInstance}
      hideMenu={true}
      size={{
        width: "66rem",
        height: "41.8rem",
        maxHeight: "90vh",
        maxWidth: "90vw",
        minWidth: "66rem",
        minHeight: "41.8rem"
      }}
      modal={false}
      version={undefined}
      swivuAssistanceConfig={undefined}
      footerRenderer={() => {
        return (
          <div className="w-full">
            <div className="flex w-full flex-col gap-4 py-3 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-1 flex-wrap items-center gap-1.5 text-[0.7rem] text-muted-foreground">
                {resourceMetaItems.length ? (
                  resourceMetaItems.map((item) => (
                    <div key={item.key} className="flex items-center gap-1.5 rounded-full border border-border/40 bg-background/60 px-2.5 py-1.5 text-left">
                      <Badge variant="outline" className="rounded-full border-transparent bg-muted/50 px-2 py-0 text-[0.55rem] font-semibold uppercase tracking-wide text-muted-foreground">
                        {item.label}
                      </Badge>
                      <span className="font-medium text-foreground/90">{item.value}</span>
                    </div>
                  ))
                ) : (
                  <span className="text-muted-foreground/70">No timeline activity yet.</span>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-end gap-2">
                {isSaving && (
                  <div className="flex items-center gap-2 text-muted-foreground/70 text-sm mr-3">
                    Saving Changes <Loader2 className="w-4 h-4 animate-spin" />
                  </div>
                )}
                <Button onClick={handleSave} disabled={!isDirty || isSaving}>
                  Save Changes
                </Button>
                {!hideDashboardButton && (
                  <Button type="button" disabled={(resourceRecord.isPhantom && !isDirty) || isSaving} onClick={() => {
                    /* If no changes in form & not phantom then directly open dashboard */
                    if (!isDirty && !resourceRecord.isPhantom) {
                      openDashboard();
                      onClose();
                    } else {
                      handleSaveAndDashboard();
                    }
                  }}>
                    Open Dashboard <i className="fa-solid fa-arrow-right -rotate-45" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      }}
      headerRenderer={headerRenderer}
      headerIconCls=""
      className={`bg-background resize-none overflow-visible border-0 ${editorInstance?.className || ""}`}
    >
      <BaseModalBody contentElementClassName="bg-transparent" className="!p-0">
        <form className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-5 space-y-6">
            {/* Info Message */}
            <div className="w-full flex items-start gap-3 text-sm rounded-md py-2.5 px-3.5  border-primary bg-card ">
              <i className="fa-solid fa-circle-info mt-0.5 flex-shrink-0 text-primary"></i>
              <span className="text-foreground/90">Resources can be people, tools, trucks, equipment, facilities, permits, and more.</span>
            </div>

            <EntityPillBar
              record={resourceRecord}
              entityConfig={ENTITY_CONFIGS[ENTITY_TYPES.RESOURCE]}
              contentElementClassName="gap-x-8 gap-y-4 flex-wrap"
              isDirectUpdates={false}
              onFieldChange={(changes) => {
                setValue(changes.fieldName as keyof ResourceFormData, changes.value, { shouldDirty: true });
              }}
              isFieldDisabled={isSaving}
              className="px-0"
              noScrollArea={true}
              itemElementClassName="flex-col justify-start items-start"
              wrapperElementClassName="p-4 bg-card rounded-md"
            />

            {/* Resource Details Section */}
            <div className="space-y-6 p-4 bg-card rounded-md">
              {/* Resource ID */}
              <div className="space-y-2">
                <Label htmlFor="name">Resource ID *</Label>
                <Controller
                  name="name"
                  control={control}
                  disabled={isSaving}
                  render={({ field }) => {
                    const DuplicateHelperComponent = SwivuDuplicateEntityHelper as React.ComponentType<{
                      record: Resource;
                      store: ISwivuStore<Resource>;
                      placeholder: string;
                      editorReactState: unknown;
                      onChange: (params: { value: string; record?: Resource }) => void;
                      disabled: boolean;
                      editorRecordKey: string;
                      inputElementClassName: string;
                      name: string;
                      value: string;
                      onBlur: () => void;
                      maxLength?: number;
                    }>;
                    return (
                      <DuplicateHelperComponent
                        {...field}
                        record={resourceRecord}
                        store={project.resourceStore}
                        placeholder="Enter resource ID"
                        editorReactState={editorInstance}
                        maxLength={RESOURCE_LABEL_MAX_LENGTH}
                        onChange={({ value, record: selectedRecord }: { value: string; record?: Resource }) => {
                          if (selectedRecord) {
                            // Reset form with all values from the selected existing resource
                            reset({
                              name: selectedRecord.name || "",
                              description: selectedRecord.description || "",
                              location: selectedRecord.location || "",
                              statusId: selectedRecord.statusId || null,
                              categoryId: selectedRecord.categoryId || null,
                              teamId: selectedRecord.teamId || null,
                              tagsId: selectedRecord.tagsId || null,
                              iconId: selectedRecord.iconId || null,
                              typeId: selectedRecord.typeId || null,
                              jobTitle: selectedRecord.jobTitle || ""
                            });

                            if (resourceRecord.isPhantom) {
                              onAfterRecordAdd?.(selectedRecord);
                            }
                          } else {
                            // Just update the name field for new resources
                            // Prevent typing if max length has been reached
                            if (value.length <= RESOURCE_LABEL_MAX_LENGTH) {
                              field.onChange(value);
                            }
                          }
                        }}
                        disabled={isSaving}
                        editorRecordKey="resource"
                        inputElementClassName="flex w-full rounded-md text-base ring-offset-background  placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-base border border-input bg-background h-swivu-input px-3 py-2"
                      />
                    );
                  }}
                />
                {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
                <p className="text-xs text-muted-foreground">
                  {nameValue?.length || 0}/{RESOURCE_LABEL_MAX_LENGTH} characters
                </p>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Controller
                  name="description"
                  control={control}
                  disabled={isSaving}
                  render={({ field }) => (
                    <Textarea
                      id="description"
                      placeholder="Required description for desktop and mobile..."
                      autoComplete="off"
                      {...field}
                      className={`min-h-[120px] resize-y ${errors.description ? "border-destructive" : ""}`}
                    />
                  )}
                />
                {errors.description && <p className="text-sm text-destructive">{errors.description.message}</p>}
                <p className="text-xs text-muted-foreground">Provide additional context or details about this resource.</p>
              </div>

              {/* Location */}
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Controller
                  name="location"
                  control={control}
                  disabled={isSaving}
                  render={({ field }) => (
                    <Input id="location" variant="background" placeholder="Enter location" autoComplete="off" {...field} className={errors.location ? "border-destructive" : ""} />
                  )}
                />
                {errors.location && <p className="text-sm text-destructive">{errors.location.message}</p>}
                <p className="text-xs text-muted-foreground">Specify the location of this resource.</p>
              </div>
            </div>
          </div>
        </form>
      </BaseModalBody>
    </BaseModal>
  );
};
